import { satisfies } from 'semver';
import type { ConfigOverrideKey, Host } from '#dn-types/host.ts';

export function getRestartRequiredSettings(meta: Host['metadata']) {
  // dnclient 0.9.6 and mobile 0.11.0 are the first releases with nebula 1.11.0
  if (
    (meta.platform === 'dnclient' && meta.version !== null && satisfies(meta.version, '>=0.9.6')) ||
    (meta.platform === 'mobile' && meta.version !== null && satisfies(meta.version, '>=0.11.0'))
  ) {
    return RESTART_REQUIRED_SETTINGS_1_11_0;
  }

  if (
    (meta.platform === 'dnclient' && meta.version !== null && satisfies(meta.version, '>=0.5.1')) ||
    (meta.platform === 'mobile' && meta.version !== null && satisfies(meta.version, '>=0.4.2'))
  ) {
    return RESTART_REQUIRED_SETTINGS_1_9_0;
  }

  return RESTART_REQUIRED_SETTINGS_DEFAULT;
}

// Both mobile clients apply definednet.dns.* only while establishing the tunnel (Android
// NebulaVpnService.startVpn, iOS PacketTunnelProvider.start) — a config reload never re-reads it,
// so a change is invisible until the tunnel restarts. Every mobile version behaves this way, hence
// all three lists below. Not to be confused with lighthouse.serve_dns, nebula's own dns responder.
const RESTART_REQUIRED_DNS_SETTINGS: ConfigOverrideKey[] = [
  'definednet.dns.resolver_addrs',
  'definednet.dns.match_domains',
  'definednet.dns.search_domains',
];

// the dns responder (including toggling serve_dns) and stats became reloadable in nebula 1.11.0
const RESTART_REQUIRED_SETTINGS_1_11_0: ConfigOverrideKey[] = [
  ...RESTART_REQUIRED_DNS_SETTINGS,
  'listen.batch',
  'listen.host',
  'listen.udp_offloads',
  'routines',
  'tun.tx_queue',
  'tun.mtu', // reloadable only on linux
  'tun.use_offloads',
];

// preferred_ranges became reloadable in nebula 1.9.0
const RESTART_REQUIRED_SETTINGS_1_9_0: ConfigOverrideKey[] = [
  ...RESTART_REQUIRED_DNS_SETTINGS,
  'lighthouse.serve_dns',
  'lighthouse.dns.host',
  'lighthouse.dns.port',
  // 'preferred_ranges',
  'listen.batch',
  'listen.host',
  'listen.udp_offloads',
  'routines',
  'tun.tx_queue',
  'tun.mtu', // reloadable only on linux
  'tun.use_offloads',
  'stats.type',
  'stats.host',
  'stats.prefix',
  'stats.namespace',
  'stats.subsystem',
  'stats.listen',
  'stats.path',
  'stats.interval',
  'stats.lighthouse_metrics',
  'stats.message_metrics',
];

// This is exported so it can be used in the tag form, where we check if any hosts are using a tag that carries the config
export const RESTART_REQUIRED_SETTINGS_DEFAULT: ConfigOverrideKey[] = [
  ...RESTART_REQUIRED_DNS_SETTINGS,
  'lighthouse.serve_dns',
  'lighthouse.dns.host',
  'lighthouse.dns.port',
  'preferred_ranges',
  'listen.batch',
  'listen.host',
  'listen.udp_offloads',
  'routines',
  'tun.tx_queue',
  'tun.mtu',
  'tun.use_offloads',
  'stats.type',
  'stats.host',
  'stats.prefix',
  'stats.namespace',
  'stats.subsystem',
  'stats.listen',
  'stats.path',
  'stats.interval',
  'stats.lighthouse_metrics',
  'stats.message_metrics',
];
